// Note: UsageProvider will be imported from gsintegrations once available
// import { UsageProvider } from "gsintegrations";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCachedPermissionContext } from "../hooks/useCachedPermissionContext";
import { useCurrentSubscriptionQuery } from "../hooks/useCurrentSubscriptionQuery";
import { useSubscriptionFeaturesQuery } from "../hooks/useSubscriptionFeaturesQuery";
import { useSubscriptionStatus } from "../hooks/useSubscriptionStatus";
import { useSubscriptionUsageQuery } from "../hooks/useSubscriptionUsageQuery";
import type {
  BooleanFeatureKey,
  SubscriptionFeatures,
  SubscriptionStatus,
  SubscriptionTier,
  SubscriptionUsageResponse,
} from "../types/subscription.types";
import { useAuth } from "./useAuth";
import { isPublicAuthFlowPath } from "../utils/publicAuthFlowPaths";

// Dynamic import for UsageProvider from gsintegrations
// This allows graceful degradation if the component isn't available yet
let UsageProvider: React.ComponentType<{
  initialPlanId: string;
  initialUsage: SubscriptionUsageResponse;
  children: React.ReactNode;
}> | null = null;

try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const gsintegrations = require("gsintegrations");
  UsageProvider = gsintegrations.UsageProvider ?? null;
} catch {
  // gsintegrations UsageProvider not available
}

type SubscriptionContextValue = {
  isLoading: boolean;
  hasActiveSubscription: boolean;
  isMissingSubscription: boolean;
  /** When true, UI should show the blocking dialog */
  forceOpenDialog: boolean;
  /** Optional CTA label for subscribe buttons */
  ctaText: string;
  /** Enhanced subscription status with state machine */
  status: SubscriptionStatus;
  /** Current subscription tier */
  tier: SubscriptionTier;
  /** All features available to the current subscription */
  features: SubscriptionFeatures | null;
  /** Whether features are still loading */
  featuresLoading: boolean;
  /** Feature flags could not be loaded, so the plan must not be treated as free */
  featuresError: boolean;
  /** Check if a boolean feature is enabled */
  hasFeature: (feature: BooleanFeatureKey) => boolean;
  /** Get a numeric limit value (-1 = unlimited) */
  getLimit: (feature: keyof SubscriptionFeatures) => number | boolean | null;
  /** Usage tracking data for quota display */
  usageTracking: SubscriptionUsageResponse | null;
  /** Whether usage data is loading */
  usageLoading: boolean;
  /** Refetch usage data (call after create/delete operations) */
  refetchUsage: () => void;
  /** Manual partner grant (no Stripe checkout) */
  isPartnerGranted: boolean;
};

const SubscriptionContext = createContext<SubscriptionContextValue | null>(
  null,
);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicAuthFlow = isPublicAuthFlowPath(location.pathname);
  // Use atomic context reader to prevent race conditions when switching profiles.
  // All subscription queries use the same coherent context snapshot.
  const {
    terreiroId: ctxTerreiroId,
    mediumId: ctxMediumId,
    isPersonalProfile,
  } = useCachedPermissionContext();

  // Derive explicit IDs for subscription queries:
  // - In terreiro mode: pass terreiroId, no mediumId
  // - In personal mode: pass mediumId, no terreiroId
  const subTerreiroId =
    !isPersonalProfile && ctxTerreiroId ? ctxTerreiroId : undefined;
  const subMediumId =
    isPersonalProfile && ctxMediumId ? ctxMediumId : undefined;

  const {
    data: subscription,
    isLoading,
    isSuccess,
    error,
  } = useCurrentSubscriptionQuery({
    terreiroId: subTerreiroId,
    mediumId: subMediumId,
    enabled: isAuthenticated && !isPublicAuthFlow,
  });

  // Enhanced subscription status with state machine
  const status = useSubscriptionStatus(subscription, isLoading, error);

  // Fetch subscription features
  const {
    data: featuresData,
    isLoading: featuresLoading,
    isError: featuresError,
  } = useSubscriptionFeaturesQuery({
      terreiroId: subTerreiroId,
      mediumId: subMediumId,
      enabled: isAuthenticated && !isPublicAuthFlow,
    });

  console.log(
    "[SubscriptionContext] Subscription features data:",
    featuresData,
  );

  // Fetch subscription usage for quota tracking
  // Pass context parameters so the query can make the API call
  const {
    data: usageData,
    isLoading: usageLoading,
    refetch: refetchUsage,
  } = useSubscriptionUsageQuery({
    terreiroId: subTerreiroId,
    mediumId: subMediumId,
    enabled: isAuthenticated && !isPublicAuthFlow,
  });

  // Debug: Log subscription context derivation
  if (typeof window !== "undefined" && window.__DEV_SUBSCRIPTION_CONTEXT__) {
    console.log("[SubscriptionContext] Context derivation:", {
      ctxTerreiroId,
      ctxMediumId,
      isPersonalProfile,
      subTerreiroId,
      subMediumId,
      isAuthenticated,
      usageLoading,
    });
  }

  // Extract tier and features from the response
  const tier: SubscriptionTier =
    featuresData?.tier ?? usageData?.tier ?? "free";
  const features: SubscriptionFeatures | null = featuresData?.features ?? null;
  const usageTracking: SubscriptionUsageResponse | null = usageData ?? null;

  // Helper to check boolean features
  const hasFeature = useCallback(
    (feature: BooleanFeatureKey): boolean => {
      if (!features) return false;
      return features[feature] === true;
    },
    [features],
  );

  // Helper to get any feature value
  const getLimit = useCallback(
    (feature: keyof SubscriptionFeatures): number | boolean | null => {
      if (!features) return null;
      return features[feature];
    },
    [features],
  );

  // We treat subscription === null (from repository) as 404/missing
  const hasActiveSubscription =
    status.metadata.hasAccess && Boolean(subscription && subscription.id);
  const isMissingSubscription =
    isSuccess && !hasActiveSubscription && status.metadata.shouldBlock;

  const [forceOpenDialog, setForceOpenDialog] = useState(false);

  // Redirect authenticated users with missing subscription based on role
  // DISABLED: Users should not be redirected when subscription is missing
  useEffect(() => {
    if (!isAuthenticated) return;

    if (isMissingSubscription) {
      // Terreiro-scoped: admins see forced dialog, members see informational page
      // Note: useHasPermission can't be called in useEffect, so we use isPersonalProfile
      // as a proxy — personal profiles are always "members" (no admin)
      if (!isPersonalProfile) {
        setForceOpenDialog(true);
      } else {
        // Personal profile users see an informational page instead of the blocking dialog
        if (forceOpenDialog) setForceOpenDialog(false);
      }
    } else if (hasActiveSubscription) {
      // Close dialog once a subscription becomes active
      if (forceOpenDialog) setForceOpenDialog(false);
    }
  }, [
    isAuthenticated,
    isMissingSubscription,
    hasActiveSubscription,
    isPersonalProfile,
    location.pathname,
    navigate,
    forceOpenDialog,
  ]);

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      isLoading,
      hasActiveSubscription,
      isMissingSubscription,
      forceOpenDialog,
      status,
      ctaText:
        subscription && (subscription as { isPartner?: boolean }).isPartner
          ? "Parceiro"
          : isMissingSubscription
            ? "Teste grátis"
            : "Gerenciar assinatura",
      tier,
      features,
      featuresLoading,
      featuresError,
      hasFeature,
      getLimit,
      usageTracking,
      usageLoading,
      refetchUsage,
      isPartnerGranted: Boolean(
        subscription &&
          ((subscription as { isPartner?: boolean }).isPartner ||
            subscription.id === "partner-entitlement"),
      ),
    }),
    [
      isLoading,
      hasActiveSubscription,
      isMissingSubscription,
      forceOpenDialog,
      status,
      subscription,
      tier,
      features,
      featuresLoading,
      featuresError,
      hasFeature,
      getLimit,
      usageTracking,
      usageLoading,
      refetchUsage,
    ],
  );

  // Wrap children with UsageProvider from gsintegrations when usage data is available
  const renderChildren = () => {
    if (usageTracking && UsageProvider) {
      return (
        <UsageProvider initialPlanId={tier} initialUsage={usageTracking}>
          {children}
        </UsageProvider>
      );
    }
    return children;
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {renderChildren()}
    </SubscriptionContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx)
    throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
};
